import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'shared-utils/src/isFunction';
import ValidIcon from 'shared-vectors/icons/Valid';
import CloseCirc from 'shared-vectors/icons/CloseCirc';
import Visible from 'shared-vectors/icons/Visible';
import Hidden from 'shared-vectors/icons/Hidden';
import { Icon } from 'shared-vectors';

import isString from 'shared-utils/src/checkVarType/isString';
import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';

import Grid from '../Grid';
import GridItem from '../GridItem';
import Spinner from '../Spinner';

import styles, { mainCls } from './styles';

const useStyles = createStyle(styles);

export default function InputField({
  className,
  type,
  name,
  value,
  placeholder,
  placeholderBack,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,  
  isValid,
  disabled,
  required,
  readOnly,
  loading,
  unstyled,
  autofocus,
  size,
  iconValid,
  iconClose,
  iconCloseAdditionAction,
  iconBefore,
  iconBeforeCls = null,
  iconAfter,
  iconAfterAction: icf,
  inputRef,
  showCloseIfQuery,
  autocorrect = null,
  inputId = null,
  ...props
}) {
  const cls = useStyles();
  const [val, setValue] = useState(value);
  const [valid, setValid] = useState(value);
  const [inputType, setInputType] = useState(type);
  const [focused, setFocused] = useState(0);
  const inputElRef = inputRef || useRef(null); // eslint-disable-line

  useEffect(() => {
    setValue(value);
    setValid(isValid(value));
  }, [isValid, value]);

  const focusToEl = () => {
    const { current } = inputElRef;
    current?.focus();
  };
  useEffect(() => {
    autofocus && focusToEl();
  }, [autofocus]); // eslint-disable-line

  const handleActionIconAfter = () => {
    if (isFunction(icf)) {
      return icf;
    }
    switch (icf) {
      case 'onfocusinput':
        return focusToEl;
      default:
        return () => { }
    }
  }
  const [iconAfterAction] = useState(handleActionIconAfter);

  const revealPwd = (evt) => {
    evt.preventDefault();
    setInputType(inputType === 'password' ? 'text' : 'password');
  }

  const handleChange = (ev) => {
    const va = ev.target.value;
    setValue(va);
    onChange(va);
  };

  const clear = (e) => {
    e.preventDefault(); // focus input after delete its value | ios fix 
    handleChange({ target: { value: '' } });
    iconCloseAdditionAction
      ? iconCloseAdditionAction()
      : focusToEl();
  };
  const inputCls = `${cls[`${mainCls}__inp`]} tp-s--l tp-w--m`;

  const preventEandDashInNumbers = (e) => {
    const ret = e.keyCode !== 69 && e.keyCode !== 189;
    if (!ret) {
      e.preventDefault();
    }
  };

  const isTypeNumber = inputType === 'number';

  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e)
    } else if (isTypeNumber) {
      preventEandDashInNumbers(e);      
    }
  };

  const inputProps = {
    value: val,
    type: inputType,
    id: inputId,
    name,
    placeholder,
    onFocus: (ev) => {
      setFocused(1);
      onFocus(ev);
    },
    onBlur: (ev) => {
      setFocused();
      onBlur(ev);
    },
    className: inputCls,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    ref: inputElRef,
    ...disabled && { disabled },
    ...required && { required },
    ...readOnly && { readOnly },
    autoCorrect: autocorrect,
    ...props,
  };

  const iconCloseRes = typeof iconClose === 'function' ? iconClose({ focused, valid }) : iconClose;
  const iconValidRes = typeof iconValid === 'function' ? iconValid({ focused, valid }) : iconValid;
  const iconAfterRes = typeof iconAfter === 'function' ? iconAfter({ focused, valid }) : iconAfter;
  const plhBackRes = typeof placeholderBack === 'function' ?
    placeholderBack({ focused, valid, value: val }) : placeholderBack;
  const hasValid = iconValidRes && valid;
  const clsStr = makeClassName([
    cls[mainCls],
    `c-bg--w c-txt--secondary-l b-r--m ${className}`,
    unstyled ? cls[`${mainCls}--unstl`] : 0,
    hasValid ? cls[`${mainCls}--valid`] : 0,
    focused ? cls[`${mainCls}--focus`] : 0,
    !focused && val ? cls[`${mainCls}--valued`] : 0,
    size ? `sizec-h--${size}` : 0,
    disabled ? 'is-inactive' : 0,
  ]);
  const iconStyle = { display: 'block' };

  const showCloseCond = showCloseIfQuery
    ? val && cls[`${mainCls}__close--shw`]
    : val && focused && cls[`${mainCls}__close--shw`];

  const iconCloseCls = !!iconCloseRes
    && makeClassName([
      'ml--s is-clickable',
      cls[`${mainCls}__close`],
      iconAfterRes && !focused && 'is-hidden', // in case input has a default icon (i.e. the pencil) close icon is hidden unless the input is focused 
      showCloseCond, // in case input has a default icon (i.e. the pencil) close icon is shown id the input is focused and has a value
    ]);

  return (
    <Grid className={clsStr} align="center">
      {
        !!iconBefore &&
        <GridItem behavior="fixed" className={iconBeforeCls || 'mr--s'}>
          { iconBefore }
        </GridItem>
      }
      <GridItem className="is-rel">
        {
          plhBackRes && val &&
          <span className={`${cls[`${mainCls}__inp-plh`]} tp--ell ${inputCls}`}>{plhBackRes}</span>
        }
        <input {...inputProps} />
      </GridItem>
      {
        !!loading &&
        <GridItem behavior="fixed">
          <Spinner block/>
        </GridItem>
      }
      {
        !!iconCloseRes && val &&
        <GridItem behavior="fixed" className={iconCloseCls} onMouseDown={clear}>
          <Icon color="f3" style={iconStyle} {...isString(iconCloseRes) ? { name: iconCloseRes } : (iconCloseRes === true ? { name: CloseCirc } : iconCloseRes)} />
        </GridItem>
      }
      {
        type === 'password' &&
        <GridItem behavior="fixed" className="p--s is-clickable" style={{ marginRight: -12 }} onMouseDown={revealPwd}>
          <Icon className="pwd" width="24px" height="24px" name={inputType === 'text' ? Visible : Hidden} />
        </GridItem>
      }
      {
        hasValid &&
        <GridItem behavior="fixed" className="ml--s">
          <Icon
            className={cls[`${mainCls}__icon-v`]}
            style={iconStyle}
            {
            ...isString(iconValidRes) ? { name: iconValidRes } :
              (iconValidRes === true ? { name: ValidIcon } : iconValidRes)
            }
          />
        </GridItem>
      }
      {
        !!iconAfterRes && !focused &&
        <GridItem behavior="fixed" className="ml--s" onClick={iconAfterAction}>
          {iconAfterRes}
        </GridItem>
      }
    </Grid>
  );
};

InputField.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Type of the input
   */
  type: PropTypes.string,

  /**
   * Input size
   */
  size: PropTypes.string,

  /**
   * Input value
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  /**
   * Input placeholder
   */
  placeholder: PropTypes.string,

  /**
   * Background placeholder (eg. used like in autocompletes)
   */
  placeholderBack: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),

  /**
   * Callback when the input is changed
   */
  onChange: PropTypes.func,

  /**
   * Callback on input focus
   */
  onFocus: PropTypes.func,

  /**
   * Callback on input blur
   */
  onBlur: PropTypes.func,

  /**
   * Check if the value is valid
   */
  isValid: PropTypes.func,

  /**
   * Disable input state
   */
  disabled: PropTypes.bool,

  /**
   * Required input
   */
  required: PropTypes.bool,

  /**
   * Readonly input
   */
  readOnly: PropTypes.bool,

  /**
   * Loading state
   */
  loading: PropTypes.bool,

  /**
   * Make the input unstyled
   */
  unstyled: PropTypes.bool,

  /**
   * Autofocus on mount
   */
  autofocus: PropTypes.bool,

  /**
   * Icon for validation
   */
  iconValid: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Close icon
   */
  iconClose: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Icon before input
   */
  iconBefore: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Icon after input
   */
  iconAfter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
  ]),

  /**
   * Define action of iconAfter
  */
  iconAfterAction: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),

  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),

  onKeyDown: PropTypes.func,
  showCloseIfQuery: PropTypes.bool,
  iconCloseAdditionAction: PropTypes.func,
  inputId: PropTypes.string,
};

InputField.defaultProps = {
  className: '',
  value: '',
  type: 'text',
  size: 'xl',
  placeholder: '',
  placeholderBack: '',
  onChange: () => { },
  onFocus: () => { },
  onBlur: () => { },
  isValid: i => i,
  disabled: false,
  required: false,
  readOnly: false,
  loading: false,
  unstyled: false,
  autofocus: false,
  iconValid: '',
  iconClose: '',
  iconBefore: '',
  iconAfter: '',
  iconAfterAction: '',
  inputRef: null,
  onKeyDown: null,
  showCloseIfQuery: false,
  iconCloseAdditionAction: null,
  inputId: null,
};